import React from 'react';
import { Box, Container } from '@mui/material';
import classNames from 'classnames';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import StackedLineChartOutlinedIcon from '@mui/icons-material/StackedLineChartOutlined';
import QueryBuilderOutlinedIcon from '@mui/icons-material/QueryBuilderOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';

import styles from './site-components/site.module.css';

const StreamComposer = () => {
  return (
    <div>
      <Container maxWidth="xl" className={classNames(styles.container)}>
        <div className={classNames(styles.scHeaderGrad, styles.headerOffset)}>
          <div className={styles.section}>
            <div className={styles.centerBlock}>
              <h1 className={styles.header}>Deliver personalized creatives at scale.</h1>
              <div className={classNames(styles.description, styles.narrow)}>
                Server-side ad technology reimagined with ultimate creative flexibility.
              </div>
            </div>

            <div className={classNames(styles.headerVideo, styles.mt6)}>
              <video
                src="https://dxgcj7vu1u7nu.cloudfront.net/web-assets/nba-pip.mp4"
                width="100%"
                height="100%"
                loop
                muted
                playsInline
                autoPlay
              />
              <div className={styles.scHeaderOverlay} />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={classNames(styles.centerBlock, styles.mt12)}>
            <h2 className={styles.tagLineHeading}>Create new inventory.</h2>
            <Box className={classNames(styles.description, styles.medium)}>
              Generate premium advertising opportunities during peak moments and lulls in action.
            </Box>
          </div>

          <div className={classNames(styles.featureGrid, styles.mt4)}>
            <div className={classNames(styles.featureBox, styles.scCreate, styles.gridTwoCol, styles.relative)}>
              <div className={classNames(styles.scCreateSpacer)} />
              <div className={styles.featureTitle}>Go beyond traditional ad breaks</div>
              <div>
                Discover how Transmit enables you to seamlessly expand ad inventory and better engage your audience.
              </div>
            </div>
            <div className={classNames(styles.featureBox, styles.scSupport)}>
              <div className={styles.featureTitle}>Support all devices and formats</div>
              <div>Stream live or VOD in HLS or DASH format, and encode to match all device sizes.</div>
              <div className={styles.scSupportSpacer} />
            </div>
            <div className={classNames(styles.featureBox, styles.scFillExisting)}>
              <div className={styles.featureTitle}>Fill existing breaks</div>
              <div>Filling traditional ad breaks is simple with our automatic SCTE detection.</div>
              <div className={styles.scFillExistingSpacer} />
            </div>
            <div className={classNames(styles.featureBox, styles.scAds, styles.gridTwoCol, styles.relative)}>
              <div className={classNames(styles.featureTitle, styles.featureTitleSmallWidth)}>
                Connect to any Ad Decision Server
              </div>
              <div className={styles.featureTitleSmallWidth}>
                Configure streams to use any Ad Decision Server, learn more about the Transmit Marketplace.
              </div>
              <div className={styles.scAdsSpacer} />
            </div>
          </div>

          <div className={classNames(styles.centerBlock, styles.mt12)}>
            <h2 className={styles.tagLineHeading}>Enhanced supply.</h2>
            <Box className={classNames(styles.description, styles.medium)}>
              Maximize content monetization and boost brand awareness.
            </Box>
          </div>

          <div className={classNames(styles.featureGrid, styles.mt4)}>
            <div className={classNames(styles.featureBox, styles.scKeyMoments)}>
              <div className={styles.featureTitle}>Premium CPM</div>
              <div>In stream inventory can be priced at 25%+ above standard advertising.</div>
              <div className={styles.scKeyMomentsSpacer} />
            </div>

            <div className={classNames(styles.featureBox, styles.scBrandAff, styles.relative)}>
              <div className={classNames(styles.featureTitle, styles.white)}>Increase Brand Affinity</div>
              <div className={classNames(styles.white)}>
                Studies reveal audiences hold a more positive view of brands that avoid interrupting their programming
                compared to those that do.
              </div>

              <div className={styles.featureStatBlock}>
                <div>
                  <div className={classNames(styles.featureStat, styles.white)}>+11.5%</div>
                  <div className={classNames(styles.featureStat, styles.featureStatSubText, styles.white)}>
                    vs pre-roll and mid-roll
                  </div>
                </div>
              </div>
            </div>

            <div
              className={classNames(styles.featureBox, styles.featureBoxNoPadding, styles.gridTwoCol, styles.scAction)}
            >
              <div className={classNames(styles.featureTitle, styles.white)}>Stay with the action</div>
              <div className={styles.white}>
                Seamlessly stitch content and ads into a single video stream to deliver targeted and individualized
                viewing experiences across LIVE and VOD content at scale.
              </div>
              <div className={classNames(styles.scActionSpacer)} />
            </div>
          </div>

          <div className={classNames(styles.centerBlock, styles.mt12)}>
            <h2 className={styles.tagLineHeading}>And much more.</h2>
          </div>

          <div className={classNames(styles.featureGrid, styles.mt4)}>
            <div className={classNames(styles.featureBox, styles.featureBoxBlank)}>
              <div>
                <BarChartOutlinedIcon className={styles.icon} />
              </div>
              <div className={styles.featureTitle}>Flexible reporting</div>
              <div>
                Quickly and easily understand the heath of your stream, monitor fill rates to maximize yield, and obtain
                and holistic view of your monetization strategy.
              </div>
            </div>
            <div className={classNames(styles.featureBox, styles.featureBoxBlank)}>
              <div>
                <StackedLineChartOutlinedIcon className={styles.icon} />
              </div>
              <div className={styles.featureTitle}>Automatic Scaling</div>
              <div>
                Stream Composer automatically scales to meet the demand of your viewers ensuring a consistent quality of
                service throughout.
              </div>
            </div>
          </div>

          <div className={classNames(styles.featureGrid, styles.mt4)}>
            <div className={classNames(styles.featureBox, styles.featureBoxBlank)}>
              <div>
                <QueryBuilderOutlinedIcon className={styles.icon} />
              </div>
              <div className={styles.featureTitle}>Efficient Encoding</div>
              <div>
                We make sure your ads are encoded just in time to maximize fill rates and tracking beacons are fired
                efficiently and accurately.
              </div>
            </div>
            <div className={classNames(styles.featureBox, styles.featureBoxBlank)}>
              <div>
                <DataObjectOutlinedIcon className={styles.icon} />
              </div>
              <div className={styles.featureTitle}>Developer Ready</div>
              <div>
                Use the Developer API to streamline your workflow even further and integrate directly into your tools
                and processes.
              </div>
            </div>
          </div>

          <div className={classNames(styles.featureGrid, styles.mt4)}>
            <div className={classNames(styles.featureBox, styles.featureBoxDark, styles.gridTwoCol, styles.scYesCase)}>
              <div className={classNames(styles.featureTitle)}>
                <img src="https://dxgcj7vu1u7nu.cloudfront.net/web-assets/yes-logo.svg" alt="YES" />
              </div>
              <div className={classNames(styles.featureTitleMedlWidth, styles.mt2, styles.white)}>
                See how the YES Network is working with Transmit to deploy new ad formats on streaming content.
              </div>
              <div className={classNames(styles.featureTitleMedlWidth, styles.mt, styles.white)}>
                <a href="https://www.sportsbusinessjournal.com/Articles/2023/11/30/yes-network-transmit">
                  Read the case study →
                </a>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default StreamComposer;
